import { WritableDraft } from 'immer/dist/types/types-external';

import { SiteSurveyStep } from '@/store/reducers/site-survey/types';
import { SiteSurveyType } from '@/types';

import { isMulteFileStep } from './surveyMap';

type SiteSurveyStepProp = Pick<
  SiteSurveyStep,
  | 'atticAccessInformation'
  | 'atticPhotos'
  | 'comments'
  | 'mainServiceDisconnectSize'
  | 'radiantBarrier'
  | 'roofDeckingVerification'
  | 'skipSheathing'
  | 'sprayFoam'
>;

export const asNewSiteStep = <T extends keyof SiteSurveyStepProp>(
  stepNumber: number,
  prop: T,
  value: SiteSurveyStep[T],
): SiteSurveyStep => {
  const defaultStep: SiteSurveyStep = {
    atticAccessInformation: null,
    atticPhotos: null,
    comments: null,
    gcsItemId: null,
    gcsBucketId: '',
    id: '',
    imageReadLink: '',
    mainServiceDisconnectSize: null,
    stepNumber,
    stepStatus: 1,
    type: SiteSurveyType.Image,
    skipSheathing: null,
    radiantBarrier: null,
    sprayFoam: null,
    roofDeckingVerification: null,
  };

  defaultStep[prop] = value;

  switch (prop) {
    case 'mainServiceDisconnectSize':
      defaultStep.type = SiteSurveyType['Main Service Disconnect Size'];
      break;
    case 'atticAccessInformation':
      defaultStep.type = SiteSurveyType['Attic Access Information'];
      break;
    case 'atticPhotos':
      defaultStep.type = SiteSurveyType['Attic Photos'];
      break;
    case 'comments':
      defaultStep.type = SiteSurveyType.Comment;
      break;
    case 'skipSheathing':
    case 'radiantBarrier':
    case 'sprayFoam':
      defaultStep.type = SiteSurveyType['Decking Details'];
      break;
    case 'roofDeckingVerification':
      defaultStep.type = SiteSurveyType['Decking Verification'];
      break;
    default:
      break;
  }

  return defaultStep;
};

/***
 * This function will mutate the original array
 * @param steps: The array of steps to be mutated
 * @param step: The step to be added or use as replacement
 * @param overrideId: The id of the step to be replaced
 */
export const replaceOrAdd = (
  steps: SiteSurveyStep[],
  step: SiteSurveyStep,
  overrideId: string | undefined,
): void => {
  if (isMulteFileStep(step.stepNumber)) {
    const index = steps.findIndex(s => s.id === overrideId);

    if (index > -1) {
      steps[index] = step;

      return;
    }
    steps.push(step);

    return;
  }

  for (let i = steps.length - 1; i > 0; i--) {
    const element = steps[i];

    if (element.stepNumber === step.stepNumber) {
      steps[i] = step;

      return;
    }
  }

  steps.push(step);
};

export const replaceOrAddAdditionalTask = (
  step: SiteSurveyStep,
  steps: WritableDraft<SiteSurveyStep[]>,
): void => {
  const index = steps.findIndex(s => s.type === step.type && s.stepNumber === step.stepNumber);

  if (index !== -1) {
    steps[index] = step;

    return;
  }

  steps.push(step);
};
